import { isSelectingSingleSelectionAndCell } from '../../bridge/utils/selectionUtils/tableSelectionPredicateUtils';
import { MilanoteHotPlugin } from './MilanoteHotPlugin';

export const EDITOR_SHORTCUTS_PLUGIN_NAME = 'EditorShortcuts';

const SHORTCUTS_GROUP_BASE_EDITOR = 'baseEditor';
const SHORTCUTS_GROUP_EDITOR = 'milanoteShortcuts.editor';
const SHORTCUTS_GROUP_EDITOR_ARROWS = 'editorManager.navigation.stopPropagation';
const SHORTCUTS_GROUP_EDITOR_MANAGER = 'editorManager.handlingEditor';
const SHORTCUTS_GROUP_NAVIGATION = 'editorManager.navigation';

/**
 * Registers Milanote-specific editor shortcuts for tables.
 */
export default class EditorShortcutsHotPlugin extends MilanoteHotPlugin {
    private editorShortcutsRegistered = false;

    static get PLUGIN_KEY(): string {
        return EDITOR_SHORTCUTS_PLUGIN_NAME;
    }

    disablePlugin(): void {
        this.unregisterShortcuts();

        super.disablePlugin();
    }

    /**
     * Registers editor-context shortcuts that must only exist while editing.
     */
    registerShortcuts(): void {
        if (this.editorShortcutsRegistered) return;

        const shortcutManager = this.milanoteHot.getShortcutManager();
        shortcutManager.setActiveContextName('editor');
        const editorContext = shortcutManager.getContext('editor');

        if (!editorContext) {
            console.warn('Unable to find Handsontable editor shortcut context.');
            return;
        }

        editorContext.removeShortcutsByKeys(['shift', 'enter']);

        const shortcutsGroupEditorOptions = {
            group: SHORTCUTS_GROUP_EDITOR,
            relativeToGroup: SHORTCUTS_GROUP_EDITOR_MANAGER,
            position: 'before' as 'before' | 'after',
        };

        const shortcutsGroupEditorArrowsOptions = {
            group: SHORTCUTS_GROUP_EDITOR_ARROWS,
            relativeToGroup: SHORTCUTS_GROUP_NAVIGATION,
            position: 'after' as 'before' | 'after',
        };

        editorContext.addShortcuts([
            {
                // Keep Ctrl+Enter inside the cell editor instead of letting Handsontable close the editor.
                keys: [['Control', 'Enter']],
                callback: () => false,
                // @ts-ignore - Handsontable incomplete TS declaration
                runOnlyIf: (event) =>
                    isSelectingSingleSelectionAndCell(this.milanoteHot.getSelected()) && !event.altKey,
                ...shortcutsGroupEditorOptions,
            },
            {
                // Keep Cmd+Enter inside the cell editor instead of letting Handsontable close the editor.
                keys: [['Meta', 'Enter']],
                callback: () => false,
                runOnlyIf: () => isSelectingSingleSelectionAndCell(this.milanoteHot.getSelected()),
                ...shortcutsGroupEditorOptions,
            },
            {
                // Keep Alt+Enter available for multiline editing instead of letting Handsontable close the editor.
                keys: [['Alt', 'Enter']],
                callback: () => false,
                runOnlyIf: () => !!this.milanoteHot.getSelected(),
                ...shortcutsGroupEditorOptions,
            },
            {
                // Stop editor-exit arrow presses from bubbling into canvas movement shortcuts.
                keys: [['ArrowUp'], ['ArrowRight'], ['ArrowDown'], ['ArrowLeft']],
                callback: () => {},
                stopPropagation: true,
                runOnlyIf: () => !!this.milanoteHot.getSelected() && !this.milanoteHot.getActiveEditor()?.isOpened(),
                ...shortcutsGroupEditorArrowsOptions,
            },
        ]);

        this.editorShortcutsRegistered = true;
    }

    /**
     * Cleans up editor shortcut groups that Handsontable recreates for each edit session.
     */
    unregisterShortcuts(): void {
        if (!this.editorShortcutsRegistered) return;

        const shortcutManager = this.milanoteHot.getShortcutManager();
        const editorContext = shortcutManager.getContext('editor');

        if (!editorContext) return;

        editorContext.removeShortcutsByGroup(SHORTCUTS_GROUP_NAVIGATION);
        editorContext.removeShortcutsByGroup(SHORTCUTS_GROUP_EDITOR);
        editorContext.removeShortcutsByGroup(SHORTCUTS_GROUP_BASE_EDITOR);

        this.editorShortcutsRegistered = false;
    }
}
